import { ROUTES } from '@/routes/paths';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/shared/components/ui/Button';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCreateSalesOrder } from '../hooks/useSalesOpsApi';
import { OrderHeaderForm } from '../components/SalesOrderBuilder/OrderHeaderForm';
import { ClientSelection } from '../components/SalesOrderBuilder/ClientSelection';
import { LineItemsTable } from '../components/SalesOrderBuilder/LineItemsTable';
import { OrderTotalsSummary } from '../components/SalesOrderBuilder/OrderTotalsSummary';
import { AdvancedOptionsSection } from '../components/SalesOrderBuilder/AdvancedOptionsSection';
import { TermsAndAttachments } from '../components/SalesOrderBuilder/TermsAndAttachments';
import { localToday } from '@/shared/utils/utils';

export interface OrderState {
  orderNo: string;
  date: string;
  logo: string | null;
  deliveredBy: { name: string; country: string };
  deliveredTo: { clientId: string | null; contactId: string | null; contactName: string };
  // ── Gap-audit follow-up: mandatory fields the client's "Sales Order
  // Created" spec names, previously only capturable one step later at the
  // Dispatch hand-off.
  customerCode: string;
  billingAddress: { street: string; city: string; state: string; zip: string; country: string };
  creditLimit: string;
  requiredDispatchDate: string;
  requiredDeliveryDate: string;
  shippingDestination: string;
  specialInstructions: string;
  shipping: {
    enabled: boolean;
    from: { warehouseId: string; sameAsBusiness: boolean; name: string; country: string; address: string; city: string; zip: string; state: string; };
    to: { addressId: string; sameAsClient: boolean; name: string; country: string; address: string; city: string; zip: string; state: string; saveToClient: boolean; };
  };
  currency: string;
  discounts: number;
  additionalCharges: number;
  customFields: Array<{ id: string; name: string; value: string; }>;
  headerCustomFields: Array<{ id: string; name: string; value: string; }>;
  terms: {
    signature: string;
    termsAndConditions: string;
    notes: string;
    attachments: string[];
    additionalInfo: string;
    contactDetails: string;
  };
  items: Array<{
    id: string;
    sku: string;
    description: string;
    image: string | null;
    hsn: string;
    gstRate: number;
    qty: number;
    rate: number;
    amount: number;
    cgst: number;
    sgst: number;
    total: number;
    // Gap-audit follow-up: Grade/Unit/Packaging/Batch requirement the spec
    // names per line — previously not captured anywhere at order creation.
    productId: string;
    unitId: string;
    unit: string;
    packSize?: string;
    qtyPerPack?: number;
    totalPackSize?: string;
    volumeLiters?: number;
    gradeId: string;
    grade: string;
    packagingTypeId: string;
    packagingType: string;
    batchRequired: boolean;
  }>;
  advancedOptions: {
    hsnView: string;
    displayUnitAs: string;
    showTaxSummary: boolean;
    hidePlaceOfSupply: boolean;
    showHsnSummary: boolean;
    addOriginalImages: boolean;
    showThumbnails: boolean;
    showDescriptionFullWidth: boolean;
    hideSubtotalForGroupItems: boolean;
    showSku: boolean;
    showSerialNumbers: boolean;
    displayBatchDetails: boolean;
  };
  gstConfig: {
    taxTreatment: string;
    taxPreference: string;
    taxCalculation: 'exclusive' | 'inclusive';
  };
  numberFormat: {
    locale: string;
    currencyDecimals: number;
  };
  tableConfig: {
    showHsn: boolean;
    showGstRate: boolean;
    showAmount: boolean;
    showCgst: boolean;
    showSgst: boolean;
  };
}

export function CreateSalesOrderPage() {
  const navigate = useNavigate();
  // A "Create Order" button on a client's own profile used to land here with
  // no context — every field started blank and the customer had to be
  // re-picked from the dropdown. ClientProfilePage now passes these through
  // (the id space matches: both it and the client dropdown below resolve to
  // the same real AccountsCustomer record).
  const [searchParams] = useSearchParams();
  // Removed showAdvanced state since it is now inline
  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync: createOrder } = useCreateSalesOrder();

  const [state, setState] = useState<OrderState>({
    orderNo: '',
    date: localToday(),
    logo: null,
    deliveredBy: { name: '', country: 'India' },
    deliveredTo: {
      clientId: searchParams.get('clientId'),
      contactId: null,
      contactName: searchParams.get('clientName') || '',
    },
    customerCode: '',
    billingAddress: { street: searchParams.get('clientAddress') || '', city: '', state: '', zip: '', country: '' },
    creditLimit: '',
    requiredDispatchDate: '',
    requiredDeliveryDate: '',
    shippingDestination: '',
    specialInstructions: '',
    shipping: {
      enabled: false,
      from: { warehouseId: '', sameAsBusiness: false, name: '', country: '', address: '', city: '', zip: '', state: '' },
      to: { addressId: '', sameAsClient: false, name: '', country: '', address: '', city: '', zip: '', state: '', saveToClient: false }
    },
    currency: 'INR',
    discounts: 0,
    additionalCharges: 0,
    customFields: [],
    headerCustomFields: [],
    terms: {
      signature: '',
      termsAndConditions: '',
      notes: '',
      attachments: [],
      additionalInfo: '',
      contactDetails: ''
    },
    items: [
      {
        id: '1', sku: '', description: '', image: null, hsn: '', gstRate: 0, qty: 1, rate: 0, amount: 0, cgst: 0, sgst: 0, total: 0,
        productId: '', unitId: '', unit: '', gradeId: '', grade: '', packagingTypeId: '', packagingType: '', batchRequired: false,
      }
    ],
    advancedOptions: {
      hsnView: 'Default',
      displayUnitAs: 'Merge with quantity',
      showTaxSummary: true,
      hidePlaceOfSupply: false,
      showHsnSummary: false,
      addOriginalImages: false,
      showThumbnails: false,
      showDescriptionFullWidth: false,
      hideSubtotalForGroupItems: false,
      showSku: false,
      showSerialNumbers: false,
      displayBatchDetails: false,
    },
    gstConfig: {
      taxTreatment: 'registered',
      taxPreference: 'taxable',
      taxCalculation: 'exclusive',
    },
    numberFormat: {
      locale: 'en-IN',
      currencyDecimals: 2,
    },
    tableConfig: {
      showHsn: true,
      showGstRate: true,
      showAmount: true,
      showCgst: true,
      showSgst: true,
    }
  });

  const updateState = (updater: (prev: OrderState) => OrderState) => {
    setState(updater);
  };

  const handleSave = async (action: 'draft' | 'new' | 'continue') => {
    try {
      setIsSaving(true);
      
      if (!state.deliveredTo.clientId) {
        toast.error('Please select a client.');
        return;
      }
      if (!state.items || state.items.length === 0 || !state.items[0].qty) {
        toast.error('Please add at least one valid line item.');
        return;
      }

      // Map OrderState to Backend SalesOrder format
      const payload = {
        documentType: 'sales_order',
        date: state.date,
        customerId: state.deliveredTo.clientId,
        shippingCharge: 0,
        additionalCharges: state.additionalCharges || 0,
        roundOff: 0,
        paymentTerms: state.terms.additionalInfo || 'Due on Receipt',
        notes: state.terms.notes,
        // Gap-audit follow-up: mandatory Sales Order fields — see OrderState's
        // comment for why these were missing at creation before now.
        customerCode: state.customerCode || undefined,
        billingAddress: Object.values(state.billingAddress).some(Boolean)
          ? { street: state.billingAddress.street, city: state.billingAddress.city, state: state.billingAddress.state, zipCode: state.billingAddress.zip, country: state.billingAddress.country }
          : undefined,
        contactPersonId: state.deliveredTo.contactId || undefined,
        contactPersonName: state.deliveredTo.contactName || undefined,
        creditLimit: state.creditLimit === '' ? undefined : Number(state.creditLimit),
        requiredDispatchDate: state.requiredDispatchDate || undefined,
        requiredDeliveryDate: state.requiredDeliveryDate || undefined,
        shippingDestination: state.shippingDestination || undefined,
        specialInstructions: state.specialInstructions || undefined,
        lines: state.items.map(item => ({
          description: item.description || item.sku,
          quantity: item.qty,
          unitPrice: item.rate,
          cgstRate: item.cgst > 0 ? item.gstRate : 0,
          sgstRate: item.sgst > 0 ? item.gstRate : 0,
          igstRate: 0, // Simplified for now
          discount: 0,
          discountType: 'fixed',
          notes: item.hsn ? `HSN: ${item.hsn}` : '',
          productId: item.productId || undefined,
          productCode: item.sku || undefined,
          unitId: item.unitId || undefined,
          unit: item.unit || undefined,
          gradeId: item.gradeId || undefined,
          grade: item.grade || undefined,
          packagingTypeId: item.packagingTypeId || undefined,
          packagingType: item.packagingType || undefined,
          batchRequired: item.batchRequired,
        }))
      };

      await createOrder(payload);
      toast.success('Sales Order saved successfully!');
      
      if (action === 'new') {
        window.location.reload(); // Quickest way to reset everything for a new order
      } else if (action === 'draft') {
        navigate(ROUTES.salesOperations);
      }
      // if 'continue', just stay on the page
      
    } catch (err: any) {
      console.error('Save error:', err);
      toast.error(err.response?.data?.error || 'Failed to save Sales Order.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-background">
      {/* Sticky action bar — this form runs long (party, lines, terms, totals,
          dispatch options), so Save must stay reachable without scrolling back
          to the top for every attempt. */}
      <header className="sticky top-0 z-20 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-4 py-3 shadow-xs md:px-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back to sales orders" className="text-on-surface-variant">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="text-label-sm uppercase tracking-wider text-on-surface-subtle">Sales</p>
            <h1 className="flex items-center gap-2 text-display-sm font-semibold text-on-surface">
              Sales Order <span className="cursor-pointer text-body-sm font-normal text-primary hover:underline">Edit</span>
            </h1>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => handleSave('draft')} disabled={isSaving}>
            Save As Draft
          </Button>
          <Button variant="outline" onClick={() => handleSave('new')} disabled={isSaving}>
            Save & Create New
          </Button>
          <Button onClick={() => handleSave('continue')} disabled={isSaving} className="gap-2">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save & Continue
          </Button>
        </div>
      </header>

      {/* Main Builder Area - Split pane layout */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="mx-auto max-w-6xl space-y-6">

          {/* 1 — Party: document header and client selection */}
          <section aria-labelledby="so-section-party" className="space-y-2">
            <h2 id="so-section-party" className="text-label-sm uppercase tracking-wider text-on-surface-subtle">Party</h2>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <OrderHeaderForm state={state} updateState={updateState} />
              <ClientSelection state={state} updateState={updateState} />
            </div>
          </section>

          {/* 2 — Lines */}
          <section aria-labelledby="so-section-lines" className="space-y-2">
            <h2 id="so-section-lines" className="text-label-sm uppercase tracking-wider text-on-surface-subtle">Line items</h2>
            <div className="overflow-hidden rounded-card border border-border bg-card shadow-xs">
              <LineItemsTable state={state} updateState={updateState} />
            </div>
          </section>

          {/* 3 — Terms and Totals */}
          <section aria-labelledby="so-section-terms" className="space-y-2">
            <h2 id="so-section-terms" className="text-label-sm uppercase tracking-wider text-on-surface-subtle">Terms and totals</h2>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <TermsAndAttachments state={state} updateState={updateState} />
              </div>
              <div className="lg:col-span-4">
                <OrderTotalsSummary state={state} updateState={updateState} />
              </div>
            </div>
          </section>

          {/* 4 — Dispatch and print options */}
          <section aria-labelledby="so-section-advanced" className="space-y-2">
            <h2 id="so-section-advanced" className="text-label-sm uppercase tracking-wider text-on-surface-subtle">Dispatch and printing</h2>
            <AdvancedOptionsSection
              state={state}
              updateState={updateState}
              onSave={handleSave}
              isSaving={isSaving}
            />
          </section>
        </div>
      </div>
    </div>
  );
}
